import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, ListChecks, CalendarDays, Disc3, BarChart3, PlugZap,
  Settings, LogOut, Menu, X, Handshake,
} from "lucide-react";

const NAV = [
  { to: "/dashboard", label: "Overview", Icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/queue", label: "Approval Queue", Icon: ListChecks, tid: "nav-queue" },
  { to: "/calendar", label: "Calendar", Icon: CalendarDays, tid: "nav-calendar" },
  { to: "/releases", label: "Releases", Icon: Disc3, tid: "nav-releases" },
  { to: "/analytics", label: "Analytics", Icon: BarChart3, tid: "nav-analytics" },
  { to: "/ade-outreach", label: "ADE Outreach", Icon: Handshake, tid: "nav-ade-outreach" },
  { to: "/connections", label: "Connections", Icon: PlugZap, tid: "nav-connections" },
  { to: "/settings", label: "Settings", Icon: Settings, tid: "nav-settings" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Auto-close the drawer when the route changes (i.e. user tapped a nav item).
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer is open.
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const currentLabel = NAV.find((n) => location.pathname.startsWith(n.to))?.label || "Alchemist Lab";

  const SidebarBody = (
    <>
      <div className="px-7 pt-8 pb-8">
        <img
          src={require("@/assets/alchemist-logo-white.png")}
          alt="Alchemist Lab"
          className="h-10 w-auto select-none"
          data-testid="brand-logo"
          draggable={false}
        />
        <div className="label-mono mt-3">Lab · Autopilot v0.2</div>
      </div>
      <nav className="px-3 flex-1 overflow-y-auto">
        {NAV.map(({ to, label, Icon, tid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={tid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 mb-1 text-sm tracking-wide transition-colors ${
                isActive
                  ? "bg-white/[0.04] text-white border-l border-[#FF3B00]"
                  : "text-white/55 hover:text-white border-l border-transparent"
              }`
            }
          >
            <Icon size={16} strokeWidth={1.5} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
      <div className="px-5 pb-6 pt-4 border-t border-white/[0.06]">
        <div className="flex items-center gap-3 mb-4">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-white/10" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center font-mono text-xs">
              {user?.name?.slice(0, 2)?.toUpperCase() || "U"}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm truncate" data-testid="nav-user-name">{user?.name || "Artist"}</div>
            <div className="label-mono truncate" data-testid="nav-user-email">{user?.email}</div>
          </div>
        </div>
        <button
          className="btn-ghost-line w-full flex items-center justify-center gap-2"
          onClick={handleLogout}
          data-testid="logout-button"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#030303] text-white lg:flex">
      {/* Desktop sidebar (>= lg) */}
      <aside className="hidden lg:flex w-[260px] shrink-0 border-r border-white/[0.06] min-h-screen flex-col sticky top-0 h-screen bg-[#030303] z-30">
        {SidebarBody}
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between px-4 h-14 border-b border-white/[0.06] bg-[#030303]/95 backdrop-blur-md">
        <button
          aria-label="Open menu"
          onClick={() => setDrawerOpen(true)}
          className="h-9 w-9 flex items-center justify-center text-white/70 hover:text-white"
          data-testid="mobile-menu-btn"
        >
          <Menu size={20} strokeWidth={1.6} />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <img
            src={require("@/assets/alchemist-logo-white.png")}
            alt="Alchemist Lab"
            className="h-6 w-auto select-none"
            draggable={false}
          />
          <span className="label-mono text-white/45 truncate ml-2">{currentLabel}</span>
        </div>
        <div className="w-9" />{/* spacer for symmetry */}
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="mobile-drawer">
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="relative w-[280px] max-w-[85vw] bg-[#030303] border-r border-white/[0.06] flex flex-col h-full animate-in slide-in-from-left duration-200">
            <button
              aria-label="Close menu"
              onClick={() => setDrawerOpen(false)}
              className="absolute top-4 right-3 h-8 w-8 flex items-center justify-center text-white/55 hover:text-white"
              data-testid="mobile-drawer-close"
            >
              <X size={18} />
            </button>
            {SidebarBody}
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0">
        <div className="max-w-[1400px] mx-auto px-4 py-6 sm:px-6 sm:py-8 md:p-12">
          {children}
        </div>
      </main>
    </div>
  );
}
